/**
 * ChatSlidePanel — Right-docked inline AI chat panel (split view)
 *
 * Sits inside the AppLayout flex row as a sibling of the main content.
 * Both sides remain interactive — no overlay/backdrop.
 *
 * Features:
 *   - Compact chat-space tabs (icon only), preserving current workspace context
 *   - Page context indicator: shows which page user is on
 *   - Quick actions: contextual prompts based on current page
 */

import { lazy, Suspense, useEffect, useMemo, useState } from 'react';
import { X, Loader2, Sparkles, MapPin, MessageSquareText, Bot } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { useAppStore } from '@/stores/app-store';
import { RenderModeContext } from '@/hooks/useRenderMode';
import { CHAT_SPACES, useChatContextStore } from '@/stores/chat-context-store';
import { resolveIcon } from '@/lib/icon-map';
import { EntityDiscussionPanel } from '@/components/entity/EntityDiscussionPanel';

const ChatPage = lazy(() => import('@/pages/ChatPage'));

export function ChatSlidePanel() {
  const { chatPanelOpen, setChatPanelOpen } = useAppStore();
  const { activeSpace, setActiveSpace, pageContext, quickActions, setAutoSwitchSpace } = useChatContextStore();
  const [panelMode, setPanelMode] = useState<'copilot' | 'thread'>('copilot');
  useTranslation();
  const currentSpaceConfig = CHAT_SPACES.find((space) => space.id === activeSpace) ?? CHAT_SPACES[0];
  const entityLabel = useMemo(() => {
    const metadata = pageContext.metadata ?? {};
    const label = metadata.entityLabel;
    if (typeof label === 'string' && label.trim().length > 0) return label;
    if (pageContext.entityType) return pageContext.entityType;
    return 'Object Thread';
  }, [pageContext.entityType, pageContext.metadata]);
  const entityTitle = useMemo(() => {
    const metadata = pageContext.metadata ?? {};
    const title = metadata.entityTitle;
    if (typeof title === 'string' && title.trim().length > 0) return title;
    return pageContext.pageLabel;
  }, [pageContext.metadata, pageContext.pageLabel]);
  const hasEntityThread = Boolean(pageContext.entityType && pageContext.entityId);

  useEffect(() => {
    if (!chatPanelOpen) return;
    setAutoSwitchSpace(false);

    return () => {
      setAutoSwitchSpace(true);
    };
  }, [chatPanelOpen, setAutoSwitchSpace]);

  useEffect(() => {
    if (!hasEntityThread && panelMode !== 'copilot') {
      setPanelMode('copilot');
    }
  }, [hasEntityThread, panelMode]);

  useEffect(() => {
    if (chatPanelOpen && hasEntityThread) {
      setPanelMode('thread');
    }
  }, [chatPanelOpen, hasEntityThread, pageContext.entityId, pageContext.entityType]);

  if (!chatPanelOpen) return null;

  return (
    <div className="flex flex-col w-[420px] shrink-0 border-l bg-background">
      {/* Header */}
      <div className="flex h-12 items-center justify-between border-b px-4 shrink-0">
        <div className="flex items-center gap-2">
          {(() => { const Icon = resolveIcon(currentSpaceConfig.iconName); return <Icon className="h-4 w-4" />; })()}
          <span className="text-sm font-semibold">{currentSpaceConfig.label}</span>
        </div>
        <button
          onClick={() => setChatPanelOpen(false)}
          className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* Space tabs */}
      <div className="flex items-center gap-1 px-2 py-1.5 border-b overflow-x-auto shrink-0">
        {CHAT_SPACES.map((space) => (
          <button
            key={space.id}
            onClick={() => setActiveSpace(space.id)}
            title={space.label}
            aria-label={space.label}
            className={cn(
              'flex h-8 w-8 shrink-0 items-center justify-center rounded-md transition-colors',
              activeSpace === space.id
                ? space.color
                : 'text-muted-foreground hover:bg-muted hover:text-foreground',
            )}
          >
            {(() => { const Icon = resolveIcon(space.iconName); return <Icon className="h-4 w-4" aria-hidden="true" />; })()}
          </button>
        ))}
      </div>

      {/* Page context indicator */}
      {pageContext.pageKey !== 'dashboard' && (
        <div className="flex items-center gap-1.5 px-3 py-1.5 bg-muted/50 text-[11px] text-muted-foreground shrink-0">
          <MapPin className="h-3 w-3" />
          <span>{pageContext.pageLabel}</span>
          {pageContext.entityId && (
            <span className="text-primary font-mono">
              #{typeof pageContext.metadata?.entityTitle === 'string' ? pageContext.metadata.entityTitle : pageContext.entityId}
            </span>
          )}
        </div>
      )}

      {/* Quick actions */}
      {quickActions.length > 0 && (
        <div className="flex flex-wrap gap-1 px-3 py-2 border-b shrink-0">
          <div className="flex items-center gap-1 text-[10px] text-muted-foreground w-full mb-1">
            <Sparkles className="h-3 w-3" />
            <span>Quick actions</span>
          </div>
          {quickActions.map((action) => (
            <button
              key={action.id}
              onClick={() => {
                window.dispatchEvent(new CustomEvent('boox:chat-prompt', { detail: { prompt: action.prompt } }));
              }}
              className="text-[11px] px-2 py-0.5 rounded-full border border-border hover:bg-primary/10 hover:border-primary/30 hover:text-primary transition-colors"
            >
              {action.label}
            </button>
          ))}
        </div>
      )}

      {hasEntityThread && (
        <div className="flex items-center gap-1 px-3 py-2 border-b shrink-0">
          <button
            onClick={() => setPanelMode('copilot')}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-medium transition-colors',
              panelMode === 'copilot'
                ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:bg-muted hover:text-foreground',
            )}
          >
            <Bot className="h-3.5 w-3.5" />
            AI Copilot
          </button>
          <button
            onClick={() => setPanelMode('thread')}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-medium transition-colors',
              panelMode === 'thread'
                ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:bg-muted hover:text-foreground',
            )}
          >
            <MessageSquareText className="h-3.5 w-3.5" />
            Object Thread
          </button>
          <div className="ml-auto truncate text-[11px] text-muted-foreground">
            {entityLabel}: {entityTitle}
          </div>
        </div>
      )}

      {/* Chat content */}
      <div className="flex flex-1 flex-col min-h-0">
        {panelMode === 'thread' && hasEntityThread ? (
          <div className="flex-1 overflow-auto p-3">
            <EntityDiscussionPanel
              entityType={pageContext.entityType!}
              entityId={pageContext.entityId!}
              entityLabel={entityLabel}
              entityTitle={entityTitle}
              active
            />
          </div>
        ) : (
          <Suspense fallback={
            <div className="flex items-center justify-center h-full">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          }>
            <RenderModeContext value="mobile">
              <ChatPage />
            </RenderModeContext>
          </Suspense>
        )}
      </div>
    </div>
  );
}
